import React, { useState, useEffect } from "react";
import "./SecurityOneHostPreApprove.scss";
import { useDocument } from "../../../../firebase-files/hooks/useDocument";
import { useAuthContext } from "../../../../firebase-files/hooks/useAuthContext";
import { useFirestore } from "../../../../firebase-files/hooks/useFirestore";
import {
  projectFirestore,
  timestamp,
} from "../../../../firebase-config/config";
import { useNavigate } from "react-router-dom";
import useNotification from "../../../../firebase-files/hooks/useNotification";
import Box from "@mui/material/Box";
import Slider from "@mui/material/Slider";

const SecurityOneHostPreApprove = () => {
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const { addDocument } = useFirestore("visitors");
  const { createNotification } = useNotification(); // Import the notification hook

  const { document, error } = useDocument(
    "m_notifications_setting",
    "visitors"
  );

  const estimateTime = {
    morning: "9:00 AM - 12:00 PM",
    afternoon: "12:00 PM - 4:00 PM",
    evening: "4:00 PM - 7:00 PM",
    custom: "Custom Time",
  };

  const [notificationViewers, setNotificationViewers] = useState([]);
  const [activeTimeTab, setActiveTimeTab] = useState("morning");
  const [mobileError, setMobileError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [totalVisitors, setTotalVisitors] = useState(1);
  const [formData, setFormData] = useState({
    visitorName: "",
    mobileNumber: "",
    purpose: "",
    remarks: "",
    visitingDateTime: "",
    estimateTime: estimateTime["morning"],
    estimateDate: "",
    customStartTime: "",
    customEndTime: "",
    hostName: "",
    hostEmail: "",
    hostMobile: "",
    totalVisitors: "",
  });
  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      estimateTime: estimateTime.morning,
    }));
    setActiveTimeTab("morning");
  }, []);

  useEffect(() => {
    if (document) {
      setNotificationViewers(document.notificationSetting.visibleTo);
    }
  }, [document]);

  useEffect(() => {
    if (user) {
      setFormData((prevData) => ({
        ...prevData,
        hostName: user.displayName || user.fullName || "", // adjust based on your user data
        hostEmail: user.email || "",
        hostMobile: user.phoneNumber || "",
      }));
    }
  }, [user]);

  const handleSliderChange = (event, newValue) => {
    setTotalVisitors(newValue);
  };
  function valuetext(value) {
    return `${value}°C`;
  }

  const purposes = [
    "Interview",
    "Relative",
    "Personal Work",
    "Courier",
    "Driver",
    "Miscellaneous",
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "mobileNumber") {
      if (!/^\d*$/.test(value)) return;
      // if (value.length > 10) return;
      // setMobileError(value.length === 10 ? '' : 'Mobile number must be 10 digits');
    }

    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleTimeTabChange = (tab) => {
    setActiveTimeTab(tab);
    if (tab !== "custom") {
      setFormData((prev) => ({
        ...prev,
        estimateTime: estimateTime[tab],
        customStartTime: "",
        customEndTime: "",
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        estimateTime: "",
      }));
    }
  };

  const handleCustomTimeChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const updatedData = {
        ...prev,
        [name]: value,
      };

      if (updatedData.customStartTime && updatedData.customEndTime) {
        updatedData.estimateTime = `${formatTime(
          updatedData.customStartTime
        )} - ${formatTime(updatedData.customEndTime)}`;
      }

      return updatedData;
    });
  };

  const formatTime = (time) => {
    const [hours, minutes] = time.split(":");
    const date = new Date();
    date.setHours(hours);
    date.setMinutes(minutes);
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsSubmitting(true);

    // Declare estimateDate outside try block
    let estimateDate = null;
    if (formData.visitingDateTime) {
      estimateDate = timestamp.fromDate(new Date(formData.visitingDateTime));
    }

    try {
      // Create user document
      await projectFirestore
        .collection("users")
        .doc(formData.mobileNumber)
        .set({
          uid: "",
          role: "customer",
          roles: ["customer"],
          online: true,
          displayName: "",
          fullName: formData.visitorName,
          gender: "",
          phoneNumber: formData.mobileNumber,
          email: formData.visitorEmail,
          city: "",
          country: "",
          countryCode: "",
          address: "",
          addresses: "",
          photoURL: "",
          status: "active",
          isAttendance: false,
          vehicleNumberPlate: "",
          vehicleStatus: "",
          vehicleType: "",
          isStaff: "",
          drivingLicence: "",
          accessType: "",
          accessValue: "",
          faceRegistration: "",
          registeredFaceUrl: "",
          createdAt: timestamp.fromDate(new Date()),
        });

      const visitorData = {
        visitorName: formData.visitorName,
        visitorEmail: formData.visitorEmail,
        phoneNumber: formData.mobileNumber,
        visitorEntryTimeStamp: null,
        visitorExitTimeStamp: null,
        hostEmail: formData.hostEmail,
        hostMobileNumber: formData.hostMobile,
        hostName: formData.hostName,
        totalVisitors: totalVisitors,
        estimateTime: formData.estimateTime,
        estimateDate: formData.estimateDate,
        purpose: formData.purpose,
        remarks: formData.remarks,
        createdAt: timestamp.fromDate(new Date()),
        isPreApprove: true,
        createdBy: user.phoneNumber,
      };
      const res = await addDocument(visitorData);
      console.log("Visitor added successfully");

      if (res) {
        console.log("Now Try to adding notification");

        // Create a notification after adding a visitor
        await createNotification({
          visibleFor: [user.phoneNumber, ...notificationViewers],
          category: "Visitor",
          heading: "New Visitor Pre-Approved",
          description: `${formData.visitorName} has been pre-approved for a visit.`,
          link: "/notifications",
          lifeSpan: document?.notificationSetting?.lifeSpan,
          metaData: visitorData, // Store visitor info in metadata
        });

        setFormData({
          visitorName: "",
          mobileNumber: "",
          purpose: "",
          remarks: "",
          visitingDateTime: "",
          estimateTime: "",
          customStartTime: "",
          customEndTime: "",
          visitorEmail: "",
          phoneNumber: "",
          visitorEntryTimeStamp: "",
          visitorExitTimeStamp: "",
          hostEmail: "",
          hostMobileNumber: "",
          hostName: "",
          totalVisitors: "",
        });
        navigate("/host-home");
      } else {
        alert("Something went wrong");
      }
    } catch (error) {
      alert("Error submitting form");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      visitorName: "",
      mobileNumber: "",
      purpose: "",
      remarks: "",
      visitingDateTime: "",
      estimateTime: "",
      customStartTime: "",
      customEndTime: "",
    });
    setMobileError("");
    navigate("/host-home");
  };

  return (
    <div className="security-one-host-pre-approve">
      <div className="security-one-host-pre-approve-header">
        <h1>Register A Visitor</h1>
        <p>Please fill this form to create a new visitor registration</p>
      </div>

      <form className="security-one-visitor-form" onSubmit={handleSubmit}>
        {/* peoples group counter start */}
        <div className="security-one-visitor-peoples-parent">
          <div
            className="d-flex justify-content-between align-items-center"
            style={{ fontWeight: "700" }}
          >
            <div
              className="security-one-visitor-peoples-decrease"
              onClick={() => setTotalVisitors((prev) => Math.max(prev - 1, 1))}
            >
              -
            </div>
            Total Visitors: {totalVisitors}
            <div
              className="security-one-visitor-peoples-decrease"
              onClick={() => setTotalVisitors((prev) => prev + 1)}
            >
              +
            </div>
          </div>
          <Box sx={{ width: 300 }}>
            <Slider
              aria-label="Temperature"
              getAriaValueText={valuetext}
              value={totalVisitors}
              onChange={handleSliderChange}
              valueLabelDisplay="auto"
              marks
              min={1}
              max={15}
            />
          </Box>
        </div>
        {/* peoples group counter end */}

        <div className="form-group">
          <label>
            Visitor Name<span className="required">*</span>
          </label>
          <input
            type="text"
            name="visitorName"
            value={formData.visitorName}
            onChange={handleChange}
            placeholder="Visitor Name"
            required
          />
        </div>
        <div className="form-group">
          <label>
            Visitor Email<span className="required">*</span>
          </label>
          <input
            type="email"
            name="visitorEmail"
            value={formData.visitorEmail}
            placeholder="Visitor Email"
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <label>
            Visitor Mobile Number<span className="required">*</span>
          </label>
          <input
            type="number"
            name="mobileNumber"
            value={formData.mobileNumber}
            onChange={handleChange}
            placeholder="Enter mobile number"
            required
          />
        </div>

        <div className="form-group">
          <label>
            Visiting Date<span className="required">*</span>
          </label>
          <input
            type="date"
            name="estimateDate"
            value={formData.estimateDate}
            onChange={handleChange}
            // required
          />
        </div>

        <div className="form-group">
          <label>
            Visiting Time Range<span className="required">*</span>
          </label>
          <div className="time-range-tabs">
            {Object.keys(estimateTime).map((tab) => (
              <button
                type="button"
                key={tab}
                className={`time-tab ${activeTimeTab === tab ? "active" : ""}`}
                onClick={() => handleTimeTabChange(tab)}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>

          {activeTimeTab === "custom" ? (
            <div className="custom-time-inputs">
              <input
                type="time"
                name="customStartTime"
                value={formData.customStartTime}
                onChange={handleCustomTimeChange}
                // required
              />
              <span>to</span>
              <input
                type="time"
                name="customEndTime"
                value={formData.customEndTime}
                onChange={handleCustomTimeChange}
                // required
              />
            </div>
          ) : (
            <div className="selected-time-range">{formData.estimateTime}</div>
          )}
        </div>

        <div className="form-group">
          <label>
            Host Name<span className="required">*</span>
          </label>
          <input
            type="text"
            name="hostName"
            value={formData.hostName}
            onChange={handleChange}
            placeholder="Host Name"
            // required
          />
        </div>

        <div className="form-group">
          <label>
            Host Email<span className="required">*</span>
          </label>
          <input
            type="email"
            name="hostEmail"
            value={formData.hostEmail}
            onChange={handleChange}
            // required
          />
        </div>

        <div className="form-group">
          <label>
            Host Mobile<span className="required">*</span>
          </label>
          <input
            type="number"
            name="hostMobile"
            value={formData.hostMobile}
            onChange={handleChange}
            // required
          />
        </div>

        <div className="form-group">
          <label>
            Purpose of Visit<span className="required">*</span>
          </label>
          <select
            name="purpose"
            value={formData.purpose}
            onChange={handleChange}
            required
          >
            <option value="">Select Purpose</option>
            {purposes.map((purpose) => (
              <option key={purpose} value={purpose}>
                {purpose}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Remarks</label>
          <textarea
            name="remarks"
            value={formData.remarks}
            onChange={handleChange}
          />
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="cancel-btn"
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className={`submit-btn ${
              isSubmitting ? "security-one-submitting-btn" : ""
            }`}
            disabled={isSubmitting}
          >
            {isSubmitting ? <>Submitting...</> : "Submit"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SecurityOneHostPreApprove;
